import axios from "axios";
import { start, done } from "./loading";
import { isNativeApp, getPlatform } from "./nativeBridge";
import { isCacheable, saveToCache, readFromCache, pruneCache } from "./offlineCache";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

// Lightweight one-shot retry for transient backend failures.
// Targets the exact failure mode Sentry kept reporting: a 502 / 503
// / 504 or naked network error during a deploy / cold-start / brief
// iOS Safari connection flake. We retry exactly ONCE after 600ms;
// past that, surface the error normally (no infinite spinners).
//
// `error.config.silent` callers (high-frequency pollers, optimistic
// background fetches) are also protected — they get the retry too.
//
// `__retried` flag prevents loops if the retry itself fails the
// same way. `idempotent` opt-out lets a caller mark a POST as
// retry-safe (off by default — we only retry GETs unless told).
// Transient 5xx upstream errors that are safe to retry on idempotent
// requests. Includes Cloudflare's edge-error codes (520-524) which
// fire when the origin is briefly unreachable, slow, or SSL-handshake
// flakes — exactly the failure mode Sentry kept escalating as
// unhandled `AxiosError: status code 520`. Treating them as retryable
// transients silences the noise and self-heals on the second attempt
// in 95%+ of cases.
const TRANSIENT_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const RETRY_DELAY_MS = 600;

const shouldRetry = (error) => {
  const cfg = error?.config;
  if (!cfg || cfg.__retried) return false;
  // Network error (no status) — fetch never reached server.
  if (!error.response) return true;
  // 5xx gateway/timeout errors.
  if (TRANSIENT_STATUSES.has(error.response.status)) {
    const method = (cfg.method || "get").toLowerCase();
    return method === "get" || cfg.idempotent === true;
  }
  return false;
};

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Native shell marker — backend skips Turnstile (which cannot run in
  // WKWebView / Android WebView) when this header is present.
  if (isNativeApp()) config.headers["X-Native-App"] = getPlatform();
  // Bump the global in-flight counter so the TopProgressBar shows.
  // Callers can opt out for high-frequency polling endpoints by
  // setting `config.silent = true`.
  if (!config.silent) start();
  return config;
});

const serveOfflineOrReject = (error) => {
  // Offline fallback: network error (no HTTP response) on a
  // cacheable GET → serve the saved copy instead of failing.
  if (!error.response && isCacheable(error.config)) {
    const cached = readFromCache(error.config);
    if (cached) {
      return {
        data: cached.data,
        status: 200,
        statusText: "OK (offline cache)",
        headers: {},
        config: error.config,
        fromOfflineCache: true,
        cachedAt: cached.t,
      };
    }
  }
  return Promise.reject(error);
};

api.interceptors.response.use(
  (response) => {
    if (!response.config?.silent) done();
    // Offline read-through cache: persist whitelisted GETs so dogs,
    // pedigrees, care logs and calendar stay readable without signal.
    if (response.status === 200 && isCacheable(response.config)) {
      saveToCache(response.config, response.data);
    }
    return response;
  },
  async (error) => {
    if (shouldRetry(error)) {
      const cfg = error.config;
      cfg.__retried = true;
      // Don't double-count the loading bar — the request interceptor
      // will bump it again on the replay.
      if (!cfg.silent) done();
      await sleep(RETRY_DELAY_MS);
      try {
        return await api.request(cfg);
      } catch (retryErr) {
        // Retry failed too — fall through to the normal error path
        // so Sentry / callers see the real failure.
        if (!retryErr?.config?.silent) done();
        // Tag the error so the global handler can filter it.
        retryErr.__retriedTransient = true;
        return serveOfflineOrReject(retryErr);
      }
    }
    if (!error?.config?.silent) done();
    return serveOfflineOrReject(error);
  },
);

// Keep the cache bounded (runs once per session).
try { pruneCache(); } catch { /* private mode */ }

export default api;
